/**
 * Collection Controls Web Component
 * Handles hide/show filters button and sort dropdown functionality
 * for both collection and search results pages
 */
class CollectionControls extends HTMLElement {
  constructor() {
    super();
    this.hideFiltersBtn = null;
    this.sortByBtn = null;
    this.sortDropdown = null;
    this.collectionWrapper = null;
    this.filtersBlock = null;
  }

  connectedCallback() {
    this.hideFiltersBtn = this.querySelector('[data-hide-filters-button]');
    this.sortByBtn = this.querySelector('[data-sort-button]');
    this.sortDropdown = this.querySelector('[data-sort-dropdown]');
    this.collectionWrapper = document.querySelector('.collection-wrapper');
    this.filtersBlock = document.querySelector('.facets-block-wrapper');

    this.setupFilterToggle();
    this.setupSortDropdown();
    this.updateSortButtonText();
  }

  setupFilterToggle() {
    if (!this.hideFiltersBtn || !this.filtersBlock || !this.collectionWrapper) {
      return;
    }

    this.hideFiltersBtn.addEventListener('click', () => {
      if (!this.collectionWrapper || !this.hideFiltersBtn) return;
      
      this.collectionWrapper.classList.toggle('filters-hidden');
      const isHidden = this.collectionWrapper.classList.contains('filters-hidden');
      this.hideFiltersBtn.setAttribute('aria-expanded', String(!isHidden));
      
      const textElement = this.hideFiltersBtn.querySelector('[data-filter-text]');
      if (textElement && textElement.textContent) {
        if (isHidden) {
          textElement.textContent = textElement.textContent.replace('Hide', 'Show');
        } else {
          textElement.textContent = textElement.textContent.replace('Show', 'Hide');
        }
      }
    });
  }

  setupSortDropdown() {
    if (!this.sortByBtn || !this.sortDropdown) {
      return;
    }

    this.sortByBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!this.sortByBtn) return;
      
      const isExpanded = this.sortByBtn.getAttribute('aria-expanded') === 'true';
      
      if (isExpanded) {
        this.closeSortDropdown();
      } else {
        this.openSortDropdown();
      }
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.sortByBtn || !this.sortDropdown || !e.target) return;
      
      const target = e.target;
      if (target instanceof Node && !this.sortByBtn.contains(target) && !this.sortDropdown.contains(target)) {
        this.closeSortDropdown();
      }
    });

    // Close dropdown on escape key
    document.addEventListener('keydown', (e) => {
      if (!this.sortByBtn) return;
      
      if (e.key === 'Escape' && this.sortByBtn.getAttribute('aria-expanded') === 'true') {
        this.closeSortDropdown();
        if (this.sortByBtn instanceof HTMLElement) {
          this.sortByBtn.focus();
        }
      }
    });
  }

  openSortDropdown() {
    if (!this.sortDropdown || !this.sortByBtn) return;
    
    if (this.sortDropdown instanceof HTMLElement) {
      this.sortDropdown.hidden = false;
    }
    this.sortByBtn.setAttribute('aria-expanded', 'true');
  }

  closeSortDropdown() {
    if (!this.sortDropdown || !this.sortByBtn) return;
    
    if (this.sortDropdown instanceof HTMLElement) {
      this.sortDropdown.hidden = true;
    }
    this.sortByBtn.setAttribute('aria-expanded', 'false');
  }

  updateSortButtonText() {
    if (!this.sortByBtn || !this.sortDropdown) return;

    // Only update if there's a sort_by parameter in the URL
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has('sort_by')) return;

    // Find the selected option (aria-selected="true")
    const selectedOption = this.sortDropdown.querySelector('[aria-selected="true"]');
    
    if (selectedOption) {
      const selectedText = selectedOption.textContent?.trim();
      const buttonTextElement = this.sortByBtn.querySelector('.collection-controls-header__sort-by-text');
      
      if (buttonTextElement && selectedText) {
        buttonTextElement.textContent = selectedText;
      }
    }
  }
}

customElements.define('collection-controls', CollectionControls);
